import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Eye, Download, TrendingUp, Sparkles, Share2, Trophy, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import GemIcon from "@/components/GemIcon";
import RarityBadge from "@/components/RarityBadge";

export default function CreatorEarnings() {
  const nav = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/creator/earnings")
      .then((r) => setData(r.data))
      .catch(() => setData({ packs: [], recent_sales: [], total_earnings_gems: 0, total_views: 0, total_imports: 0, published_count: 0 }));
  }, []);

  const fmtDate = (s) => (s ? new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");
  const titleFor = (slug) => (data?.packs || []).find((p) => p.slug === slug)?.title || "A study pack";
  const topCreator = data && data.total_earnings_gems >= 100;

  return (
    <div className="max-w-[1100px] mx-auto px-5 md:px-8 py-10 md:py-14" data-testid="creator-earnings-page">
      <button
        onClick={() => nav(-1)}
        className="text-[13px] font-medium text-[#B0AEBC] hover:text-white flex items-center gap-1 mb-8"
        data-testid="earnings-back"
      >
        <ArrowLeft size={14} /> Back
      </button>

      <motion.header
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-start justify-between flex-wrap gap-4 mb-8"
      >
        <div>
          <div className="eyebrow">Creator studio</div>
          <h1 className="display-2 mt-2 flex items-center gap-3">
            Creator <span className="grad-text">earnings</span>
            {topCreator && (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-[0.14em]"
                style={{ background: "rgba(255,216,107,0.16)", border: "1px solid rgba(255,216,107,0.4)", color: "#FFD86B" }}
                data-testid="top-creator-badge">
                <Trophy size={12} /> Bundle &amp; sell
              </span>
            )}
          </h1>
          <p className="text-[15px] text-[#B0AEBC] mt-3 max-w-lg">
            Every time someone imports one of your published packs, you earn gems. Here's how your studies are performing.
          </p>
        </div>
        <Link to="/app/my-packs" className="btn btn-secondary px-5 py-2.5 text-[13px] self-start" data-testid="earnings-manage-packs">
          <Share2 size={14} /> Manage packs
        </Link>
      </motion.header>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-10">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
          className="surface p-5 col-span-2 lg:col-span-1 relative overflow-hidden" data-testid="earnings-total">
          <div className="absolute -top-10 -right-10 w-28 h-28 rounded-full pointer-events-none"
            style={{ background: "radial-gradient(circle, rgba(124,92,255,0.35), transparent 65%)", filter: "blur(18px)" }} />
          <div className="relative">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8B99] mb-2">Total earned</div>
            <div className="flex items-center gap-2">
              <GemIcon size={26} />
              <span className="text-[34px] font-bold tabular-nums leading-none">{data?.total_earnings_gems ?? "—"}</span>
            </div>
            <div className="text-[12px] text-[#8E8B99] mt-2">gems from pack sales</div>
          </div>
        </motion.div>

        <Stat icon={Download} tone="#5AC8FA" label="Imports" value={data?.total_imports} testId="earnings-imports" />
        <Stat icon={Eye} tone="#B8A4FF" label="Views" value={data?.total_views} testId="earnings-views" />
        <Stat icon={Sparkles} tone="#7C5CFF" label="Published" value={data?.published_count} testId="earnings-published" />
      </div>

      {/* Empty state */}
      {data && data.published_count === 0 && (
        <div className="surface p-10 text-center" data-testid="earnings-empty">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#7C5CFF]/25 to-[#5AC8FA]/15 border border-white/10 flex items-center justify-center mx-auto mb-4"
            style={{ boxShadow: "0 0 24px -8px rgba(124,92,255,0.55)" }}>
            <TrendingUp size={20} className="text-[#B8A4FF]" />
          </div>
          <h3 className="text-[17px] font-semibold text-white">Start earning gems</h3>
          <p className="text-[13.5px] text-[#B0AEBC] mt-2 max-w-sm mx-auto">
            Publish a study to the community marketplace. Each import pays you gems — set your own price when you publish.
          </p>
          <Link to="/app/studies">
            <button className="btn btn-primary mt-5 px-5 py-2.5 text-[13.5px]" data-testid="earnings-publish-cta">
              <Sparkles size={13} /> Pick a study to publish
            </button>
          </Link>
        </div>
      )}

      {/* Per-pack breakdown */}
      {data && data.published_count > 0 && (
        <section className="mb-10">
          <h2 className="text-[18px] font-semibold mb-4">By pack</h2>
          <div className="space-y-2.5">
            {data.packs.map((p, i) => (
              <motion.div
                key={p.slug}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
                className="surface p-4 flex items-center gap-4"
                data-testid={`earning-pack-${p.slug}`}
              >
                <div className="min-w-0 flex-1">
                  <Link to={`/packs/${p.slug}`} target="_blank" className="inline-flex items-center gap-1.5 group">
                    <span className="text-[14.5px] font-semibold text-white group-hover:text-[#B8A4FF] transition-colors truncate">{p.title}</span>
                    <ExternalLink size={12} className="text-[#8E8B99] shrink-0" />
                  </Link>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                    {p.rarity && <RarityBadge rarity={p.rarity} size="sm" />}
                    {p.is_course && <span className="chip chip-blue">Course</span>}
                    <span className="text-[11.5px] text-[#8E8B99]">{fmtDate(p.created_at)}</span>
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-1.5 text-[13px] text-[#B0AEBC]">
                  <Eye size={13} /> <span className="tabular-nums font-semibold text-white">{p.views || 0}</span>
                </div>
                <div className="hidden sm:flex items-center gap-1.5 text-[13px] text-[#B0AEBC]">
                  <Download size={13} className="text-[#5AC8FA]" /> <span className="tabular-nums font-semibold text-white">{p.imports || 0}</span>
                </div>
                <div className="flex items-center gap-1.5 min-w-[88px] justify-end">
                  <GemIcon size={15} />
                  <span className="tabular-nums font-bold text-[15px]">{p.earnings_gems || 0}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      )}

      {/* Recent sales feed */}
      {data && (data.recent_sales || []).length > 0 && (
        <section data-testid="recent-sales">
          <h2 className="text-[18px] font-semibold mb-4">Recent sales</h2>
          <div className="space-y-2">
            {data.recent_sales.map((s, i) => (
              <div key={i} className="flex items-center justify-between gap-3 surface px-4 py-3" data-testid={`sale-${i}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#7C5CFF]/30 to-[#5AC8FA]/15 border border-white/10 flex items-center justify-center shrink-0">
                    <Download size={14} className="text-[#5AC8FA]" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-[13.5px] font-medium text-white truncate">{titleFor(s.ref_slug)}</div>
                    <div className="text-[11.5px] text-[#8E8B99]">Imported by another learner · {fmtDate(s.created_at)}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <GemIcon size={13} />
                  <span className="tabular-nums font-bold text-[14px] text-[#B8A4FF]">+{s.delta}</span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {!data && (
        <div className="grid sm:grid-cols-2 gap-3">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-20 shimmer rounded-2xl" />)}
        </div>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, tone, label, value, testId }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="surface p-5" data-testid={testId}>
    <Icon size={16} style={{ color: tone }} className="mb-2" />
    <div className="text-[11px] font-semibold uppercase tracking-wider text-[#8E8B99]">{label}</div>
    <div className="text-[26px] font-bold tabular-nums mt-0.5">{value ?? "—"}</div>
  </motion.div>
);
